// Night. A sleeping phone shows the same quiet screen all night (no progress, no timers, no
// counters) so it reveals nothing about who else is awake. A woken phone shows its prompt. The
// one exception is the host's phone, which the server tells when a player's turn has stalled.
import { useEffect, useState } from "react";
import { CharacterPicker } from "../components/CharacterPicker";
import { Countdown } from "../components/Countdown";
import { InfoView } from "../components/InfoView";
import { PhaseHeader } from "../components/PhaseHeader";
import { TownSquare } from "../components/TownSquare";
import { useCommand } from "../hooks/useCommand";
import type { PlayerView } from "../protocol";
import { seatName } from "../state/selectors";
import { useGame } from "../state/store";

export function NightSleepingScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const waitingOn = view.night?.waiting_on_seat_id;
  return (
    <main className="screen night">
      <PhaseHeader view={view} roles={roles} />
      <div className="grow stack center" style={{ justifyContent: "center" }}>
        <div className="moon" aria-hidden />
        <h2>Night {view.night_number}</h2>
        <p className="muted">Eyes on your own phone.</p>
        <p className="small faint">If the Storyteller needs you, this screen will change.</p>
      </div>
      {waitingOn && <WaitingOn view={view} seatId={waitingOn} />}
    </main>
  );
}

/** The host's phone: whose turn the night is stuck on, so the host can tell them to look. */
function WaitingOn({ view, seatId }: { view: PlayerView; seatId: string }) {
  const offline = view.seats.find((s) => s.seat_id === seatId)?.connected === false;
  return (
    <section className="card card-tight stack-sm waiting-on" role="status">
      <span className="eyebrow">The night is waiting on</span>
      <h3>{seatName(view, seatId)}</h3>
      <p className="small">
        {offline
          ? "Their phone is offline: the app may be closed or the screen locked. Ask them to open it."
          : "Their turn has been open for a while. Ask them to check their phone."}
      </p>
      <p className="tiny faint">Only the host sees this.</p>
    </section>
  );
}

// The Storyteller shook their head at a player who died or came back earlier tonight (the town
// square shows that only at dawn): this screen says so itself, instead of a toast.
const HEAD_SHAKE: readonly string[] = ["head_shake"];

export function NightPromptScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const prompt = view.private.prompt!;
  const { run, busy } = useCommand(HEAD_SHAKE);
  const [selected, setSelected] = useState<string[]>([]);
  const [chosenRoles, setChosenRoles] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Players to tap on the town square, a character from a list, or both (the Gambler's guess).
  const choosesSeats = prompt.max_targets > 0;
  const choosesRole = prompt.role_options.length > 0;
  // A prompt that can take no choice at all offers Nobody, which is tapped like a seat.
  const offersNobody = prompt.min_targets === 0 && (!choosesRole || prompt.min_roles === 0);
  const [nobody, setNobody] = useState(false);
  useEffect(() => {
    setSelected([]);
    setChosenRoles([]);
    setNobody(false);
    setRefusal(null);
  }, [prompt.prompt_id]);

  const confirm = async () => {
    const result = await run({
      type: "submit_night_choice",
      prompt_id: prompt.prompt_id,
      target_seat_ids: nobody ? [] : selected,
      role_ids: nobody ? [] : chosenRoles,
    });
    if (result.kind === "error" && result.engine_code === "head_shake") {
      setRefusal(result.message);
      setSelected([]); // choose again
    }
  };

  const toggle = (seatId: string) => {
    setNobody(false);
    setSelected((cur) => {
      if (cur.includes(seatId)) return cur.filter((s) => s !== seatId);
      if (prompt.max_targets === 1) return [seatId];
      if (cur.length >= prompt.max_targets) return cur;
      return [...cur, seatId];
    });
  };

  const toggleRole = (roleId: string) => {
    setNobody(false);
    setChosenRoles((cur) => (cur.includes(roleId) ? [] : [roleId]));
  };

  const toggleNobody = () => {
    setSelected([]);
    setChosenRoles([]);
    setNobody((cur) => !cur);
  };

  const least = choosesSeats ? Math.max(prompt.min_targets, 1) : 0;
  const seatsReady = selected.length >= least && selected.length <= prompt.max_targets;
  const rolesReady = !choosesRole || chosenRoles.length >= Math.max(prompt.min_roles, 1);
  const ready = nobody || (seatsReady && rolesReady);
  const need = least - selected.length;
  const waitingFor =
    need > 0 ? `Choose ${need} more` : choosesRole && !rolesReady ? "Choose a character" : "";

  return (
    <main className="screen night awake">
      <PhaseHeader view={view} roles={roles} />
      <div className="stack-sm">
        <div className="row-between">
          <span className="eyebrow">You are awake</span>
          <Countdown deadline={prompt.deadline_ms} className="small muted" />
        </div>
        <h2>{prompt.title}</h2>
        <p>{prompt.text}</p>
        {refusal && (
          <p className="error-text head-shake" role="alert">
            {refusal}
          </p>
        )}
      </div>
      {choosesSeats && (
        <TownSquare
          view={view}
          candidates={prompt.candidate_seat_ids}
          selected={selected}
          onSeatTap={toggle}
        >
          {nobody ? (
            <span className="small">Nobody</span>
          ) : selected.length > 0 ? (
            <span className="small">{selected.map((id) => seatName(view, id)).join(" + ")}</span>
          ) : (
            <span className="small muted">
              Tap {prompt.max_targets > 1 ? "players" : "a player"}
            </span>
          )}
        </TownSquare>
      )}
      {choosesRole && (
        <CharacterPicker
          roleIds={prompt.role_options}
          roles={roles}
          selected={chosenRoles}
          onToggle={toggleRole}
        />
      )}
      {offersNobody && (
        <button
          type="button"
          className="btn btn-ghost btn-block"
          aria-pressed={nobody}
          onClick={toggleNobody}
        >
          Nobody
        </button>
      )}
      <div className="grow" />
      <div className="bottom-bar">
        <button className="btn btn-primary" disabled={!ready || busy} onClick={confirm}>
          {ready ? "Confirm" : waitingFor}
        </button>
        <p className="tiny faint center">
          {prompt.auto_pick
            ? "If time runs out, a choice is made for you."
            : "If time runs out, this is skipped."}
        </p>
      </div>
    </main>
  );
}

export function NightInfoScreen({ view }: { view: PlayerView }) {
  const roles = useGame((s) => s.roles);
  const prompt = view.private.prompt!;
  const { run, busy } = useCommand();
  return (
    <main className="screen night awake">
      <PhaseHeader view={view} roles={roles} />
      <div className="row-between">
        <span className="eyebrow">You are awake</span>
        <Countdown deadline={prompt.deadline_ms} className="small muted" />
      </div>
      <div className="card stack">
        <h2>{prompt.title}</h2>
        {prompt.info ? (
          <InfoView entry={prompt.info} view={view} roles={roles} showStamp={false} />
        ) : (
          <p style={{ fontSize: "1.05rem" }}>{prompt.text}</p>
        )}
      </div>
      {prompt.info && <p className="tiny faint">This is saved in your notebook.</p>}
      <div className="grow" />
      <div className="bottom-bar">
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={() => run({ type: "ack_info", prompt_id: prompt.prompt_id })}
        >
          Got it, back to sleep
        </button>
      </div>
    </main>
  );
}
